const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Shared helper: draw a node circle + label ────────────────────
function Node({ x, y, label, fill, stroke, textFill, r = 16, fontSize = 12 }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={fill || '#EBF3FB'} stroke={stroke || '#2E75B6'} strokeWidth="2"/>
      <text x={x} y={y + fontSize * 0.38} textAnchor="middle" fontSize={fontSize}
        fill={textFill || '#1F3864'} fontWeight="700" fontFamily="'Courier New',monospace">{label}</text>
    </g>
  );
}

// ── Shared helper: draw an edge (line) with optional arrow ───────
function Edge({ x1, y1, x2, y2, color, sw, dashed, arrow, label, labelOffset }) {
  const c = color || '#888';
  const s = sw || 1.8;
  const dx = x2 - x1, dy = y2 - y1, len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return null;
  const ux = dx / len, uy = dy / len;
  const r = 16;
  const sx = x1 + ux * r, sy = y1 + uy * r;
  const ex = x2 - ux * r, ey = y2 - uy * r;
  const mx = (sx + ex) / 2, my = (sy + ey) / 2;
  const lo = labelOffset || { dx: 0, dy: -10 };
  return (
    <g>
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={c} strokeWidth={s}
        strokeDasharray={dashed ? '5,3' : undefined}/>
      {arrow && (
        <polygon points={`${ex},${ey} ${ex - 8*ux + 4*(-uy)},${ey - 8*uy + 4*ux} ${ex - 8*ux - 4*(-uy)},${ey - 8*uy - 4*ux}`}
          fill={c}/>
      )}
      {label && (
        <text x={mx + lo.dx} y={my + lo.dy} textAnchor="middle" fontSize="11"
          fill={c} fontWeight="700" fontFamily="'Courier New',monospace">{label}</text>
      )}
    </g>
  );
}

// ── Diagram 1: Graph Types Taxonomy ─────────────────────────────
function DiagramGraphTypes() {
  const W = 900, H = 310;
  const cols = 3, rows = 2;
  const PW = (W - 28 - (cols - 1) * 10) / cols;
  const PH = (H - 20 - (rows - 1) * 10) / rows;
  const px = (c) => 14 + c * (PW + 10);
  const py = (r) => 10 + r * (PH + 10);

  // Panel configs: [col, row, title, color, description, graph drawing fn]
  // We'll draw small representative graphs in each panel

  // Undirected: pentagon
  const undirNodes = [0,1,2,3,4].map(i => ({
    x: Math.round(Math.cos((i * 2 * Math.PI / 5) - Math.PI/2) * 44 + PW/2),
    y: Math.round(Math.sin((i * 2 * Math.PI / 5) - Math.PI/2) * 44 + PH/2 + 8),
    label: String.fromCharCode(65 + i),
  }));
  const undirEdges = [[0,1],[1,2],[2,3],[3,4],[4,0],[0,2]];

  // Directed: 4 nodes in a square with directed edges
  const dirNodes = [
    { x: PW/2-36, y: PH/2-24+8, label:'A' },
    { x: PW/2+36, y: PH/2-24+8, label:'B' },
    { x: PW/2+36, y: PH/2+32+8, label:'C' },
    { x: PW/2-36, y: PH/2+32+8, label:'D' },
  ];
  const dirEdges = [[0,1],[1,2],[2,3],[3,0],[0,2]];

  // Weighted: 4 nodes triangle+1
  const wNodes = [
    { x: PW/2-40, y: PH/2+20+8, label:'A' },
    { x: PW/2,    y: PH/2-34+8, label:'B' },
    { x: PW/2+40, y: PH/2+20+8, label:'C' },
    { x: PW/2,    y: PH/2+8,    label:'D' },
  ];
  const wEdges = [[0,1,'4'],[1,2,'7'],[0,2,'9'],[0,3,'2'],[1,3,'3'],[2,3,'5']];

  // Complete K4
  const k4Nodes = [0,1,2,3].map(i => ({
    x: Math.round(Math.cos((i * Math.PI/2) - Math.PI/4) * 44 + PW/2),
    y: Math.round(Math.sin((i * Math.PI/2) - Math.PI/4) * 44 + PH/2 + 8),
    label: String.fromCharCode(65 + i),
  }));
  const k4Edges = [[0,1],[0,2],[0,3],[1,2],[1,3],[2,3]];

  // Bipartite K_{2,3}
  const biLeft  = [{ x: PW/2-48, y: PH/2-22+8, label:'A' }, { x: PW/2-48, y: PH/2+22+8, label:'B' }];
  const biRight = [{ x: PW/2+48, y: PH/2-36+8, label:'X' }, { x: PW/2+48, y: PH/2+8, label:'Y' }, { x: PW/2+48, y: PH/2+36+8, label:'Z' }];
  const biEdges = [[0,0],[0,1],[0,2],[1,0],[1,1],[1,2]]; // [leftIdx, rightIdx]

  // Multigraph: 3 nodes, multiple edges between same pair
  const mulNodes = [
    { x: PW/2-46, y: PH/2+8, label:'A' },
    { x: PW/2+46, y: PH/2+8, label:'B' },
    { x: PW/2,    y: PH/2-42+8, label:'C' },
  ];

  const panels = [
    { c:0, r:0, title:'Undirected Graph', color:'#2E75B6', bg:'#EBF3FB', desc:'Edges have no direction. deg(v) = number of edges at v.' },
    { c:1, r:0, title:'Directed Graph (Digraph)', color:'#27ae60', bg:'#f0fff4', desc:'Edges are ordered pairs (u,v). In-degree and out-degree distinct.' },
    { c:2, r:0, title:'Weighted Graph', color:'#e07b00', bg:'#fff7ee', desc:'Each edge carries a numerical weight (cost, distance, capacity).' },
    { c:0, r:1, title:'Complete Graph K4', color:'#9b59b6', bg:'#f5d0ff', desc:'Every pair of vertices connected. K_n has n(n-1)/2 edges.' },
    { c:1, r:1, title:'Bipartite Graph K_{2,3}', color:'#c0392b', bg:'#fff0f0', desc:'Vertices split into two sets; edges only cross between sets.' },
    { c:2, r:1, title:'Multigraph', color:'#1F3864', bg:'#e8eaf6', desc:'Multiple edges (parallel) and/or self-loops allowed.' },
  ];

  const nr = 12; // node radius

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Graph Types — Undirected, Directed, Weighted, Complete, Bipartite, Multigraph</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {panels.map(({ c, r, title, color, desc }, pi) => {
          const bx = px(c), by = py(r);
          return (
            <g key={pi}>
              <rect x={bx} y={by} width={PW} height={PH} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <rect x={bx} y={by} width={PW} height={26} rx={5} fill={color}/>
              <rect x={bx} y={by+18} width={PW} height={8} fill={color}/>
              <text x={bx+PW/2} y={by+17} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={bx+PW/2} y={by+PH-10} textAnchor="middle" fontSize="9.5" fill="#666">{desc}</text>
            </g>
          );
        })}

        {/* Panel 0: Undirected */}
        {undirEdges.map(([a,b],i) => (
          <line key={i}
            x1={px(0)+undirNodes[a].x} y1={py(0)+undirNodes[a].y}
            x2={px(0)+undirNodes[b].x} y2={py(0)+undirNodes[b].y}
            stroke="#2E75B6" strokeWidth="1.8"/>
        ))}
        {undirNodes.map((n,i) => (
          <Node key={i} x={px(0)+n.x} y={py(0)+n.y} label={n.label} r={nr}
            fill="#EBF3FB" stroke="#2E75B6" textFill="#1F3864"/>
        ))}

        {/* Panel 1: Directed */}
        {dirEdges.map(([a,b],i) => {
          const na = dirNodes[a], nb = dirNodes[b];
          const bx1 = px(1), by1 = py(0);
          return <Edge key={i} x1={bx1+na.x} y1={by1+na.y} x2={bx1+nb.x} y2={by1+nb.y} color="#27ae60" arrow sw={1.8}/>;
        })}
        {dirNodes.map((n,i) => (
          <Node key={i} x={px(1)+n.x} y={py(0)+n.y} label={n.label} r={nr}
            fill="#f0fff4" stroke="#27ae60" textFill="#1a6640"/>
        ))}

        {/* Panel 2: Weighted */}
        {wEdges.map(([a,b,w],i) => {
          const na = wNodes[a], nb = wNodes[b];
          const bx2 = px(2), by2 = py(0);
          const mx = (na.x+nb.x)/2+bx2, my = (na.y+nb.y)/2+by2;
          return (
            <g key={i}>
              <line x1={bx2+na.x} y1={by2+na.y} x2={bx2+nb.x} y2={by2+nb.y}
                stroke="#e07b00" strokeWidth="1.6"/>
              <rect x={mx-8} y={my-8} width={16} height={14} rx={2} fill="#fff7ee"/>
              <text x={mx} y={my+2} textAnchor="middle" fontSize="10" fill="#e07b00"
                fontWeight="700" fontFamily="'Courier New',monospace">{w}</text>
            </g>
          );
        })}
        {wNodes.map((n,i) => (
          <Node key={i} x={px(2)+n.x} y={py(0)+n.y} label={n.label} r={nr}
            fill="#fff7ee" stroke="#e07b00" textFill="#a05000"/>
        ))}

        {/* Panel 3: Complete K4 */}
        {k4Edges.map(([a,b],i) => {
          const na = k4Nodes[a], nb = k4Nodes[b];
          return <line key={i} x1={px(0)+na.x} y1={py(1)+na.y} x2={px(0)+nb.x} y2={py(1)+nb.y}
            stroke="#9b59b6" strokeWidth="1.6"/>;
        })}
        {k4Nodes.map((n,i) => (
          <Node key={i} x={px(0)+n.x} y={py(1)+n.y} label={n.label} r={nr}
            fill="#f5d0ff" stroke="#9b59b6" textFill="#6c2fa0"/>
        ))}

        {/* Panel 4: Bipartite */}
        {biEdges.map(([li,ri],i) => {
          const nl = biLeft[li], nr2 = biRight[ri];
          return <line key={i} x1={px(1)+nl.x} y1={py(1)+nl.y} x2={px(1)+nr2.x} y2={py(1)+nr2.y}
            stroke="#c0392b" strokeWidth="1.5"/>;
        })}
        {biLeft.map((n,i) => (
          <Node key={'L'+i} x={px(1)+n.x} y={py(1)+n.y} label={n.label} r={nr}
            fill="#fff0f0" stroke="#c0392b" textFill="#8b0000"/>
        ))}
        {biRight.map((n,i) => (
          <Node key={'R'+i} x={px(1)+n.x} y={py(1)+n.y} label={n.label} r={nr}
            fill="#fff0f0" stroke="#c0392b" textFill="#8b0000"/>
        ))}
        <text x={px(1)+PW/2-48+8} y={py(1)+26+16} textAnchor="middle" fontSize="9" fill="#c0392b">Set L</text>
        <text x={px(1)+PW/2+48-8} y={py(1)+26+16} textAnchor="middle" fontSize="9" fill="#c0392b">Set R</text>

        {/* Panel 5: Multigraph */}
        {/* Self-loop on A */}
        <ellipse cx={px(2)+mulNodes[0].x-12} cy={py(1)+mulNodes[0].y}
          rx={14} ry={10} fill="none" stroke="#1F3864" strokeWidth="1.6"/>
        {/* Two parallel edges A-B */}
        <path d={`M ${px(2)+mulNodes[0].x+nr} ${py(1)+mulNodes[0].y-6} C ${px(2)+mulNodes[0].x+40} ${py(1)+mulNodes[0].y-26} ${px(2)+mulNodes[1].x-40} ${py(1)+mulNodes[1].y-26} ${px(2)+mulNodes[1].x-nr} ${py(1)+mulNodes[1].y-6}`}
          fill="none" stroke="#1F3864" strokeWidth="1.6"/>
        <path d={`M ${px(2)+mulNodes[0].x+nr} ${py(1)+mulNodes[0].y+6} C ${px(2)+mulNodes[0].x+40} ${py(1)+mulNodes[0].y+26} ${px(2)+mulNodes[1].x-40} ${py(1)+mulNodes[1].y+26} ${px(2)+mulNodes[1].x-nr} ${py(1)+mulNodes[1].y+6}`}
          fill="none" stroke="#1F3864" strokeWidth="1.6"/>
        <line x1={px(2)+mulNodes[0].x+nr*0.7} y1={py(1)+mulNodes[0].y-nr*0.7}
          x2={px(2)+mulNodes[2].x-nr*0.7} y2={py(1)+mulNodes[2].y+nr*0.7}
          stroke="#1F3864" strokeWidth="1.6"/>
        <line x1={px(2)+mulNodes[1].x-nr*0.7} y1={py(1)+mulNodes[1].y-nr*0.7}
          x2={px(2)+mulNodes[2].x+nr*0.7} y2={py(1)+mulNodes[2].y+nr*0.7}
          stroke="#1F3864" strokeWidth="1.6"/>
        {mulNodes.map((n,i) => (
          <Node key={i} x={px(2)+n.x} y={py(1)+n.y} label={n.label} r={nr}
            fill="#e8eaf6" stroke="#1F3864" textFill="#1F3864"/>
        ))}
        <text x={px(2)+mulNodes[0].x-14} y={py(1)+mulNodes[0].y-22} fontSize="9" fill="#1F3864">loop</text>
        <text x={px(2)+(mulNodes[0].x+mulNodes[1].x)/2} y={py(1)+mulNodes[0].y-32}
          textAnchor="middle" fontSize="9" fill="#1F3864">parallel</text>

      </svg>
      <div style={S.dCaption}>A graph G = (V, E) consists of a set of vertices V and a set of edges E. Undirected graphs have unordered edge pairs; directed graphs (digraphs) have ordered pairs. Weighted graphs assign values to edges. K_n is the complete graph on n vertices with every pair connected. Bipartite graphs split vertices into two independent sets. Multigraphs allow parallel edges and self-loops.</div>
    </div>
  );
}

// ── Diagram 2: Graph Representations ────────────────────────────
function DiagramRepresentations() {
  const W = 900, H = 340;
  const LP = 14, LW = 240;
  const MP = LP + LW + 14, MW = 290;
  const RP = MP + MW + 14, RW = W - RP - 14;

  // The graph: 4 nodes A,B,C,D
  // Edges: A-B, A-C, B-C, B-D, C-D
  const gNodes = [
    { x: 80,  y: 80,  label:'A' },
    { x: 160, y: 60,  label:'B' },
    { x: 80,  y: 160, label:'C' },
    { x: 160, y: 160, label:'D' },
  ];
  const gEdges = [[0,1],[0,2],[1,2],[1,3],[2,3]];
  const nr = 16;

  // Adjacency matrix (4x4)
  const adj = [
    [0,1,1,0],
    [1,0,1,1],
    [1,1,0,1],
    [0,1,1,0],
  ];
  const matLabels = ['A','B','C','D'];
  const cellSize = 30;
  const matX = MP + 42, matY = 46;

  // Adjacency list
  const adjList = [
    { v:'A', neighbors:'B, C' },
    { v:'B', neighbors:'A, C, D' },
    { v:'C', neighbors:'A, B, D' },
    { v:'D', neighbors:'B, C' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Graph Representations — Adjacency Matrix, Adjacency List, and Edge List</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel: the graph drawing */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Graph G</text>
        {gEdges.map(([a,b],i) => (
          <line key={i}
            x1={LP+gNodes[a].x} y1={40+gNodes[a].y}
            x2={LP+gNodes[b].x} y2={40+gNodes[b].y}
            stroke="#2E75B6" strokeWidth="2"/>
        ))}
        {gNodes.map((n,i) => (
          <Node key={i} x={LP+n.x} y={40+n.y} label={n.label} r={nr}
            fill="#EBF3FB" stroke="#2E75B6" textFill="#1F3864"/>
        ))}
        {/* Degree annotations */}
        <text x={LP+12} y={H-70} fontSize="11" fill="#1F3864" fontWeight="700">Degrees:</text>
        {['A:2','B:3','C:3','D:2'].map((d,i) => (
          <text key={i} x={LP+12} y={H-55+i*14} fontSize="11" fill="#555"
            fontFamily="'Courier New',monospace">  {d}</text>
        ))}
        <text x={LP+12} y={H-14} fontSize="10" fill="#888">Sum = 10 = 2|E|</text>

        {/* Middle panel: adjacency matrix */}
        <rect x={MP} y={10} width={MW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={MP+MW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Adjacency Matrix</text>

        {/* Row/col header labels */}
        {matLabels.map((lbl, i) => (
          <g key={i}>
            <text x={matX + (i+1)*cellSize + cellSize/2} y={matY+12}
              textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">{lbl}</text>
            <text x={matX+cellSize/2-4} y={matY+(i+1)*cellSize+cellSize/2+4}
              textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">{lbl}</text>
          </g>
        ))}

        {/* Matrix cells */}
        {adj.map((row, r) =>
          row.map((val, c) => {
            const cx = matX + (c+1)*cellSize;
            const cy = matY + (r+1)*cellSize;
            return (
              <g key={`${r}-${c}`}>
                <rect x={cx} y={cy} width={cellSize-1} height={cellSize-1} rx={2}
                  fill={val===1 ? '#EBF3FB' : '#fafafa'}
                  stroke={val===1 ? '#2E75B6' : '#e0e0e0'}
                  strokeWidth={val===1 ? 1.5 : 0.8}/>
                <text x={cx+cellSize/2-1} y={cy+cellSize/2+4}
                  textAnchor="middle" fontSize="12"
                  fill={val===1 ? '#2E75B6' : '#ccc'}
                  fontWeight={val===1 ? '700' : '400'}
                  fontFamily="'Courier New',monospace">{val}</text>
              </g>
            );
          })
        )}

        {/* Matrix properties */}
        <rect x={MP+10} y={matY+5*cellSize+10} width={MW-20} height={56} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={MP+18} y={matY+5*cellSize+26} fontSize="11" fill="#1F3864" fontWeight="700">Properties of adj. matrix A:</text>
        <text x={MP+18} y={matY+5*cellSize+41} fontSize="10" fill="#444">Symmetric (undirected): A = A^T</text>
        <text x={MP+18} y={matY+5*cellSize+55} fontSize="10" fill="#444">Row sum = degree of vertex</text>

        <rect x={MP+10} y={matY+5*cellSize+72} width={MW-20} height={40} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={MP+18} y={matY+5*cellSize+88} fontSize="10" fill="#e07b00" fontWeight="700">Space: O(V^2)  |  Lookup: O(1)</text>
        <text x={MP+18} y={matY+5*cellSize+102} fontSize="10" fill="#555">Best for dense graphs</text>

        {/* Right panel: adjacency list */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Adjacency List</text>

        {adjList.map(({ v, neighbors }, i) => {
          const y = 42 + i * 44;
          return (
            <g key={i}>
              {/* Head node box */}
              <rect x={RP+14} y={y} width={28} height={28} rx={3}
                fill="#27ae60" stroke="#27ae60" strokeWidth="1"/>
              <text x={RP+28} y={y+18} textAnchor="middle" fontSize="13" fill="#fff"
                fontWeight="700" fontFamily="'Courier New',monospace">{v}</text>
              {/* Arrow to list */}
              <line x1={RP+42} y1={y+14} x2={RP+56} y2={y+14} stroke="#27ae60" strokeWidth="1.5"/>
              <polygon points={`${RP+56},${y+14} ${RP+50},${y+10} ${RP+50},${y+18}`} fill="#27ae60"/>
              {/* Neighbor boxes */}
              {neighbors.split(', ').map((nb, j) => {
                const bx = RP + 60 + j * 46;
                return (
                  <g key={j}>
                    <rect x={bx} y={y} width={38} height={28} rx={3}
                      fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
                    <text x={bx+19} y={y+18} textAnchor="middle" fontSize="12" fill="#27ae60"
                      fontWeight="700" fontFamily="'Courier New',monospace">{nb}</text>
                    {j < neighbors.split(', ').length - 1 && (
                      <line x1={bx+38} y1={y+14} x2={bx+46} y2={y+14}
                        stroke="#27ae60" strokeWidth="1.2"/>
                    )}
                  </g>
                );
              })}
              {/* NULL terminator */}
              <text x={RP+60+neighbors.split(', ').length*46+6} y={y+18}
                fontSize="10" fill="#aaa" fontFamily="'Courier New',monospace">∅</text>
            </g>
          );
        })}

        {/* Space/time box */}
        <rect x={RP+10} y={42+4*44+8} width={RW-20} height={54} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={RP+18} y={42+4*44+24} fontSize="11" fill="#27ae60" fontWeight="700">Space: O(V + E)  |  Lookup: O(deg(v))</text>
        <text x={RP+18} y={42+4*44+40} fontSize="10" fill="#444">Best for sparse graphs</text>
        <text x={RP+18} y={42+4*44+55} fontSize="10" fill="#555">Edge list: just store pairs (u,v) — O(E) space</text>

      </svg>
      <div style={S.dCaption}>Three standard ways to represent a graph: adjacency matrix (O(V²) space, O(1) lookup), adjacency list (O(V+E) space, best for sparse graphs), and edge list (O(E) space, simplest but slow for neighbor queries). For an undirected graph, the adjacency matrix is symmetric and each row's sum equals the vertex's degree. The handshaking lemma states the sum of all degrees equals twice the number of edges.</div>
    </div>
  );
}

// ── Diagram 3: BFS and DFS Traversal ────────────────────────────
function DiagramBFSDFS() {
  const W = 900, H = 360;
  const LP = 14, LW = (W - 42) / 2;
  const RP = LP + LW + 14, RW = W - RP - 14;

  // Shared source graph (6 nodes)
  // Layout: source=1 at top, then 2,3 at level2, then 4,5,6 at level3
  function makeGraph(ox, oy) {
    return {
      nodes: [
        { id:1, x:ox+100, y:oy+36,  label:'1' },
        { id:2, x:ox+44,  y:oy+106, label:'2' },
        { id:3, x:ox+156, y:oy+106, label:'3' },
        { id:4, x:ox+16,  y:oy+186, label:'4' },
        { id:5, x:ox+90,  y:oy+186, label:'5' },
        { id:6, x:ox+166, y:oy+186, label:'6' },
      ],
      edges: [[1,2],[1,3],[2,4],[2,5],[3,5],[3,6]],
    };
  }

  const bfsG = makeGraph(LP + 20, 0);
  const dfsG = makeGraph(RP + 20, 0);

  // BFS order from node 1: 1,2,3,4,5,6 (level by level)
  const bfsOrder = [1,2,3,4,5,6];
  // BFS tree edges
  const bfsTree = [[1,2],[1,3],[2,4],[2,5],[3,6]];
  // Cross/non-tree edges in BFS
  const bfsCross = [[3,5]];

  // DFS order from node 1: 1,2,4,5,3,6
  const dfsOrder = [1,2,4,5,3,6];
  const dfsTree  = [[1,2],[2,4],[4,5],[1,3],[3,6]]; // back edges shown dashed
  // Non-tree in DFS
  const dfsBack  = [[5,3]];

  const bfsColors = ['#2E75B6','#27ae60','#27ae60','#e07b00','#e07b00','#9b59b6'];
  const dfsColors = ['#2E75B6','#27ae60','#e07b00','#e07b00','#27ae60','#9b59b6'];

  const nr = 15;

  function findNode(g, id) { return g.nodes.find(n => n.id === id); }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Graph Traversal — Breadth-First Search (BFS) and Depth-First Search (DFS)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── BFS panel ── */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Breadth-First Search (BFS)</text>
        <text x={LP+LW/2} y={42} textAnchor="middle" fontSize="11" fill="#555">Start: node 1  |  Visit order: 1→2→3→4→5→6</text>

        {/* BFS cross edges (dashed) */}
        {bfsCross.map(([a,b],i) => {
          const na = findNode(bfsG, a), nb = findNode(bfsG, b);
          return <line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#ccc" strokeWidth="1.5" strokeDasharray="4,3"/>;
        })}
        {/* BFS tree edges */}
        {bfsTree.map(([a,b],i) => {
          const na = findNode(bfsG, a), nb = findNode(bfsG, b);
          return <line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#2E75B6" strokeWidth="2.2"/>;
        })}
        {/* BFS nodes colored by visit order (level) */}
        {bfsG.nodes.map((n,i) => {
          const ord = bfsOrder.indexOf(n.id);
          return (
            <g key={i}>
              <Node x={n.x} y={n.y} label={n.label} r={nr}
                fill={bfsColors[ord] ? bfsColors[ord]+'33' : '#EBF3FB'}
                stroke={bfsColors[ord] || '#2E75B6'} textFill="#1F3864"/>
              <text x={n.x+nr+6} y={n.y+4} fontSize="11"
                fill={bfsColors[ord] || '#888'} fontWeight="700">#{ord+1}</text>
            </g>
          );
        })}

        {/* BFS level labels */}
        <text x={LP+14} y={36+36} fontSize="10" fill="#aaa">Lv 0</text>
        <text x={LP+14} y={106+36} fontSize="10" fill="#aaa">Lv 1</text>
        <text x={LP+14} y={186+36} fontSize="10" fill="#aaa">Lv 2</text>

        {/* BFS legend */}
        <line x1={LP+14} y1={H-54} x2={LP+38} y2={H-54} stroke="#2E75B6" strokeWidth="2.2"/>
        <text x={LP+44} y={H-50} fontSize="10" fill="#555">Tree edge</text>
        <line x1={LP+14} y1={H-36} x2={LP+38} y2={H-36} stroke="#ccc" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={LP+44} y={H-32} fontSize="10" fill="#555">Cross edge</text>
        <rect x={LP+120} y={H-62} width={LW-130} height={52} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+128} y={H-48} fontSize="10" fill="#1F3864" fontWeight="700">BFS uses a Queue (FIFO)</text>
        <text x={LP+128} y={H-34} fontSize="10" fill="#444">Finds shortest path in</text>
        <text x={LP+128} y={H-20} fontSize="10" fill="#444">unweighted graphs. O(V+E)</text>

        {/* ── DFS panel ── */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Depth-First Search (DFS)</text>
        <text x={RP+RW/2} y={42} textAnchor="middle" fontSize="11" fill="#555">Start: node 1  |  Visit order: 1→2→4→5→3→6</text>

        {/* DFS back edges (dashed) */}
        {dfsBack.map(([a,b],i) => {
          const na = findNode(dfsG, a), nb = findNode(dfsG, b);
          return <line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>;
        })}
        {/* DFS tree edges */}
        {dfsTree.map(([a,b],i) => {
          const na = findNode(dfsG, a), nb = findNode(dfsG, b);
          return <line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#27ae60" strokeWidth="2.2"/>;
        })}
        {/* DFS nodes colored by visit order */}
        {dfsG.nodes.map((n,i) => {
          const ord = dfsOrder.indexOf(n.id);
          return (
            <g key={i}>
              <Node x={n.x} y={n.y} label={n.label} r={nr}
                fill={dfsColors[ord] ? dfsColors[ord]+'33' : '#f0fff4'}
                stroke={dfsColors[ord] || '#27ae60'} textFill="#1F3864"/>
              <text x={n.x+nr+6} y={n.y+4} fontSize="11"
                fill={dfsColors[ord] || '#888'} fontWeight="700">#{ord+1}</text>
            </g>
          );
        })}

        {/* DFS legend */}
        <line x1={RP+14} y1={H-54} x2={RP+38} y2={H-54} stroke="#27ae60" strokeWidth="2.2"/>
        <text x={RP+44} y={H-50} fontSize="10" fill="#555">Tree edge</text>
        <line x1={RP+14} y1={H-36} x2={RP+38} y2={H-36} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={RP+44} y={H-32} fontSize="10" fill="#555">Back edge (cycle)</text>
        <rect x={RP+134} y={H-62} width={RW-144} height={52} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={RP+142} y={H-48} fontSize="10" fill="#1F3864" fontWeight="700">DFS uses a Stack (LIFO)</text>
        <text x={RP+142} y={H-34} fontSize="10" fill="#444">Detects cycles; back edges</text>
        <text x={RP+142} y={H-20} fontSize="10" fill="#444">indicate cycles. O(V+E)</text>

      </svg>
      <div style={S.dCaption}>BFS explores layer by layer using a queue, guaranteeing that the first time a node is reached gives the shortest unweighted path from the source. DFS dives as deep as possible using a stack (or recursion), producing a DFS tree where back edges identify cycles. Both run in O(V+E). BFS is used for shortest paths; DFS underpins topological sort, strongly connected components (Kosaraju/Tarjan), and articulation points.</div>
    </div>
  );
}

// ── Diagram 4: Trees and Spanning Trees ─────────────────────────
function DiagramTrees() {
  const W = 900, H = 370;
  const LP = 14, LW = 290;
  const MP = LP + LW + 14, MW = 270;
  const RP = MP + MW + 14, RW = W - RP - 14;
  const nr = 15;

  // Left: rooted tree anatomy
  // Root at top, 3 children, some with children
  const treeNodes = [
    { id:'r', x:LP+LW/2,  y:48,   label:'r', role:'root' },
    { id:'a', x:LP+60,   y:118,  label:'a', role:'internal' },
    { id:'b', x:LP+LW/2, y:118,  label:'b', role:'internal' },
    { id:'c', x:LP+220,  y:118,  label:'c', role:'leaf' },
    { id:'d', x:LP+28,   y:194,  label:'d', role:'leaf' },
    { id:'e', x:LP+96,   y:194,  label:'e', role:'leaf' },
    { id:'f', x:LP+LW/2, y:194,  label:'f', role:'leaf' },
    { id:'g', x:LP+200,  y:194,  label:'g', role:'leaf' },
  ];
  const treeEdges = [['r','a'],['r','b'],['r','c'],['a','d'],['a','e'],['b','f'],['c','g']];

  function findTN(id) { return treeNodes.find(n => n.id === id); }

  // Kruskal's MST — weighted graph 5 nodes
  const mstNodes = [
    { x:MP+60,      y:60,  label:'A' },
    { x:MP+MW-60,   y:60,  label:'B' },
    { x:MP+MW/2,    y:135, label:'C' },
    { x:MP+40,      y:210, label:'D' },
    { x:MP+MW-40,   y:210, label:'E' },
  ];
  // All edges with weights
  const allEdges = [
    [0,1,4,'AB'],[0,2,2,'AC'],[0,3,6,'AD'],
    [1,2,3,'BC'],[1,4,5,'BE'],
    [2,3,1,'CD'],[2,4,4,'CE'],
    [3,4,2,'DE'],
  ];
  // Kruskal sorted: CD=1, AC=2, DE=2, BC=3, AB=4, CE=4, BE=5, AD=6
  // MST edges: CD(1), AC(2), DE(2), BC(3) → connects all 5 nodes
  const mstEdges = new Set(['CD','AC','DE','BC']);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Trees — Rooted Tree Anatomy and Minimum Spanning Tree (Kruskal's Algorithm)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left: rooted tree */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Rooted Tree Anatomy</text>

        {treeEdges.map(([a,b],i) => {
          const na = findTN(a), nb = findTN(b);
          return <line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#2E75B6" strokeWidth="1.8"/>;
        })}
        {treeNodes.map((n,i) => {
          const isRoot = n.role==='root';
          const isLeaf = n.role==='leaf';
          return (
            <Node key={i} x={n.x} y={n.y} label={n.label} r={nr}
              fill={isRoot?'#2E75B6':isLeaf?'#f0fff4':'#EBF3FB'}
              stroke={isRoot?'#1F3864':'#2E75B6'}
              textFill={isRoot?'#fff':'#1F3864'}/>
          );
        })}

        {/* Annotations */}
        <rect x={LP+LW-84} y={38} width={76} height={20} rx={2} fill="#2E75B6" fillOpacity="0.15"/>
        <text x={LP+LW-46} y={52} textAnchor="middle" fontSize="9" fill="#2E75B6" fontWeight="700">Root (depth 0)</text>

        <line x1={LP+LW-20} y1={115} x2={LP+LW-8} y2={115} stroke="#e07b00" strokeWidth="1"/>
        <text x={LP+LW-6} y={119} fontSize="9" fill="#e07b00" fontWeight="700" textAnchor="start">depth 1</text>
        <line x1={LP+LW-20} y1={191} x2={LP+LW-8} y2={191} stroke="#27ae60" strokeWidth="1"/>
        <text x={LP+LW-6} y={195} fontSize="9" fill="#27ae60" fontWeight="700" textAnchor="start">depth 2</text>

        {/* Properties box */}
        <rect x={LP+8} y={H-110} width={LW-16} height={98} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+16} y={H-95} fontSize="11" fill="#1F3864" fontWeight="700">Tree properties:</text>
        <text x={LP+16} y={H-79} fontSize="10" fill="#444">Connected + acyclic  (|E| = |V| - 1)</text>
        <text x={LP+16} y={H-63} fontSize="10" fill="#444">Unique path between any two vertices</text>
        <text x={LP+16} y={H-47} fontSize="10" fill="#444">Root = top node;  Leaves = degree-1 nodes</text>
        <text x={LP+16} y={H-31} fontSize="10" fill="#444">Height = max depth;  Subtree = node + descendants</text>
        <text x={LP+16} y={H-15} fontSize="10" fill="#555">Binary tree: each node has at most 2 children</text>

        {/* Middle: MST with Kruskal */}
        <rect x={MP} y={10} width={MW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={MP+MW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Kruskal's MST</text>
        <text x={MP+MW/2} y={43} textAnchor="middle" fontSize="10" fill="#555">Greedily add cheapest edge that doesn't form a cycle</text>

        {allEdges.map(([a,b,w,name],i) => {
          const na = mstNodes[a], nb = mstNodes[b];
          const isMST = mstEdges.has(name);
          const mx = (na.x+nb.x)/2, my = (na.y+nb.y)/2;
          return (
            <g key={i}>
              <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={isMST ? '#27ae60' : '#ddd'}
                strokeWidth={isMST ? 2.8 : 1.4}/>
              <rect x={mx-8} y={my-8} width={16} height={14} rx={2}
                fill={isMST ? '#f0fff4' : '#fff'}/>
              <text x={mx} y={my+2} textAnchor="middle" fontSize="10"
                fill={isMST ? '#27ae60' : '#bbb'}
                fontWeight={isMST ? '700' : '400'}
                fontFamily="'Courier New',monospace">{w}</text>
            </g>
          );
        })}
        {mstNodes.map((n,i) => (
          <Node key={i} x={n.x} y={n.y} label={n.label} r={nr}
            fill="#f0fff4" stroke="#27ae60" textFill="#1a6640"/>
        ))}

        {/* Kruskal steps */}
        <rect x={MP+8} y={238} width={MW-16} height={108} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={MP+16} y={254} fontSize="11" fill="#27ae60" fontWeight="700">Kruskal steps (sorted edges):</text>
        {[
          '1. CD (w=1): add  ✓',
          '2. AC (w=2): add  ✓',
          '3. DE (w=2): add  ✓',
          '4. BC (w=3): add  ✓  (MST complete)',
          '5. AB (w=4): skip (cycle)',
        ].map((step, i) => (
          <text key={i} x={MP+16} y={270+i*18} fontSize="10" fill={i<4?'#27ae60':'#c0392b'}
            fontFamily="'Courier New',monospace">{step}</text>
        ))}
        <text x={MP+16} y={H-16} fontSize="10" fill="#555">Total MST weight = 1+2+2+3 = 8</text>

        {/* Right: Prim's + other tree facts */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Spanning Trees</text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6', color:'#2E75B6',
            t:"Spanning tree",  n:"Subgraph that is a tree containing\nall V vertices.  Has V-1 edges." },
          { y:96,  bg:'#f0fff4', bc:'#27ae60', color:'#27ae60',
            t:"MST (Min Span Tree)", n:"Spanning tree with minimum total\nedge weight." },
          { y:156, bg:'#fff7ee', bc:'#e07b00', color:'#e07b00',
            t:"Kruskal's Algorithm", n:"Sort edges by weight; use Union-Find\nto skip cycle-forming edges. O(E log E)" },
          { y:216, bg:'#fff0f0', bc:'#c0392b', color:'#c0392b',
            t:"Prim's Algorithm",   n:"Grow tree from a seed vertex; greedily\nadd min-weight edge to frontier. O(E log V)" },
          { y:276, bg:'#f5d0ff', bc:'#9b59b6', color:'#9b59b6',
            t:"Number of span trees", n:"Kirchhoff's theorem: det of any\n(V-1)x(V-1) cofactor of Laplacian L" },
        ].map(({ y, bg, bc, color, t, n }) => (
          <g key={y}>
            <rect x={RP+8} y={y} width={RW-16} height={54} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+16} y={y+16} fontSize="11" fill={color} fontWeight="700">{t}</text>
            {n.split('\n').map((ln, j) => (
              <text key={j} x={RP+16} y={y+32+j*14} fontSize="10" fill="#444">{ln}</text>
            ))}
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>A tree is a connected acyclic graph; with n vertices it has exactly n-1 edges and a unique path between any pair of vertices. A spanning tree covers all vertices of a connected graph. Kruskal's algorithm builds the MST by greedily adding the cheapest non-cycle-forming edge (using Union-Find for cycle detection, O(E log E)). Prim's grows a tree from a seed vertex using a priority queue (O(E log V)).</div>
    </div>
  );
}

// ── Diagram 5: Euler & Hamiltonian Paths ────────────────────────
function DiagramEulerHamilton() {
  const W = 900, H = 350;
  const PW = (W - 42) / 2;
  const pL = 14, pR = pL + PW + 14;
  const nr = 15;

  // Left: Königsberg bridge problem — 4 landmasses, 7 bridges
  // Simplified: 4 nodes representing landmasses, multigraph
  const kNodes = [
    { x:pL+PW/2,    y:90,  label:'N' },  // North bank
    { x:pL+PW/2,    y:230, label:'S' },  // South bank
    { x:pL+PW/2-86, y:160, label:'W' }, // West island
    { x:pL+PW/2+86, y:160, label:'E' }, // East island
  ];
  // 7 bridges (multi-edges) — degrees: N=3, S=3, W=5, E=3 (all odd!)
  // Represented schematically
  const kEdgePaths = [
    // N-W (2 bridges)
    `M ${kNodes[0].x} ${kNodes[0].y+nr} Q ${kNodes[0].x-40} ${(kNodes[0].y+kNodes[2].y)/2} ${kNodes[2].x+nr} ${kNodes[2].y}`,
    `M ${kNodes[0].x} ${kNodes[0].y+nr} Q ${kNodes[0].x-70} ${(kNodes[0].y+kNodes[2].y)/2} ${kNodes[2].x+nr} ${kNodes[2].y-8}`,
    // S-W (2 bridges)
    `M ${kNodes[1].x} ${kNodes[1].y-nr} Q ${kNodes[1].x-40} ${(kNodes[1].y+kNodes[2].y)/2} ${kNodes[2].x+nr} ${kNodes[2].y+4}`,
    `M ${kNodes[1].x} ${kNodes[1].y-nr} Q ${kNodes[1].x-70} ${(kNodes[1].y+kNodes[2].y)/2} ${kNodes[2].x+nr} ${kNodes[2].y+12}`,
    // N-E (1 bridge)
    `M ${kNodes[0].x} ${kNodes[0].y+nr} Q ${kNodes[0].x+40} ${(kNodes[0].y+kNodes[3].y)/2} ${kNodes[3].x-nr} ${kNodes[3].y}`,
    // S-E (1 bridge)
    `M ${kNodes[1].x} ${kNodes[1].y-nr} Q ${kNodes[1].x+40} ${(kNodes[1].y+kNodes[3].y)/2} ${kNodes[3].x-nr} ${kNodes[3].y}`,
    // W-E (1 bridge)
    `M ${kNodes[2].x+nr} ${kNodes[2].y} L ${kNodes[3].x-nr} ${kNodes[3].y}`,
  ];

  // Right: Hamiltonian path example on K4
  const hamNodes = [0,1,2,3].map(i => ({
    x: pR + 80 + Math.round(Math.cos((i*Math.PI/2) - Math.PI/4) * 72),
    y: 170 + Math.round(Math.sin((i*Math.PI/2) - Math.PI/4) * 72),
    label: String.fromCharCode(65+i),
  }));
  // All K4 edges
  const k4All = [[0,1],[0,2],[0,3],[1,2],[1,3],[2,3]];
  // Hamiltonian cycle: A→B→C→D→A
  const hamCycle = [[0,1],[1,2],[2,3],[3,0]];
  const hamSet = new Set(hamCycle.map(([a,b])=>`${a}-${b}`));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler Paths & Circuits — Königsberg Bridges — and Hamiltonian Paths</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={pL} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pL+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Euler Path / Circuit</text>
        <text x={pL+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Königsberg Bridges — no Euler circuit exists</text>

        {/* Bridge edges */}
        {kEdgePaths.map((d,i) => (
          <path key={i} d={d} fill="none" stroke="#c0392b" strokeWidth="2"/>
        ))}
        {/* Landmass nodes */}
        {kNodes.map((n,i) => (
          <g key={i}>
            <Node x={n.x} y={n.y} label={n.label} r={nr+2}
              fill="#fff0f0" stroke="#c0392b" textFill="#8b0000" fontSize={13}/>
          </g>
        ))}
        {/* Degree labels */}
        {[['N','deg=3'],['S','deg=3'],['W','deg=5'],['E','deg=3']].map(([lbl,deg],i) => (
          <text key={i} x={kNodes[i].x + (i===2?-48:i===3?46:0)}
            y={kNodes[i].y + (i===0?-24:i===1?28:4)}
            textAnchor="middle" fontSize="10" fill="#c0392b">{deg}</text>
        ))}

        {/* Euler conditions */}
        <rect x={pL+10} y={270} width={PW-20} height={64} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={pL+18} y={286} fontSize="11" fill="#c0392b" fontWeight="700">Euler Circuit: ALL degrees must be even</text>
        <text x={pL+18} y={302} fontSize="10" fill="#444">Here all 4 nodes have ODD degree → no Euler circuit</text>
        <text x={pL+18} y={318} fontSize="11" fill="#27ae60" fontWeight="700">Euler Path: exactly 0 or 2 vertices of odd degree</text>
        <text x={pL+18} y={330} fontSize="10" fill="#555">0 odd-degree vertices → circuit;  2 → path (start/end at odd)</text>

        {/* Right panel */}
        <rect x={pR} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pR+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Hamiltonian Path / Circuit</text>
        <text x={pR+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">K4 has a Hamiltonian circuit: A→B→C→D→A</text>

        {/* All K4 edges (gray) */}
        {k4All.map(([a,b],i) => {
          const key = `${a}-${b}`;
          const isHam = hamSet.has(key);
          return (
            <line key={i}
              x1={hamNodes[a].x} y1={hamNodes[a].y}
              x2={hamNodes[b].x} y2={hamNodes[b].y}
              stroke={isHam ? '#27ae60' : '#ddd'}
              strokeWidth={isHam ? 2.8 : 1.4}/>
          );
        })}
        {/* Hamiltonian circuit arrows */}
        {hamCycle.map(([a,b],i) => {
          const na = hamNodes[a], nb = hamNodes[b];
          return (
            <text key={i}
              x={(na.x+nb.x)/2 + (nb.y-na.y)*0.18}
              y={(na.y+nb.y)/2 - (nb.x-na.x)*0.18}
              textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">
              {i+1}
            </text>
          );
        })}
        {hamNodes.map((n,i) => (
          <Node key={i} x={n.x} y={n.y} label={n.label} r={nr}
            fill="#f0fff4" stroke="#27ae60" textFill="#1a6640"/>
        ))}

        {/* Comparison table */}
        <rect x={pR+10} y={270} width={PW-20} height={64} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={pR+18} y={286} fontSize="11" fill="#27ae60" fontWeight="700">Hamiltonian circuit: visits every VERTEX exactly once</text>
        <text x={pR+18} y={302} fontSize="10" fill="#444">Euler visits every EDGE; Hamiltonian visits every VERTEX</text>
        <text x={pR+18} y={318} fontSize="10" fill="#c0392b" fontWeight="700">Deciding Hamiltonian circuit: NP-complete (no easy criterion)</text>
        <text x={pR+18} y={332} fontSize="10" fill="#555">Dirac: if every vertex has degree ≥ n/2, circuit exists</text>

      </svg>
      <div style={S.dCaption}>Euler's Königsberg Bridge problem (1736) asked whether all 7 bridges could be crossed exactly once — impossible because all 4 landmasses have odd degree. An Euler circuit (visiting every edge once and returning to start) exists iff all vertices have even degree. A Hamiltonian circuit visits every vertex exactly once; deciding its existence is NP-complete, though sufficient conditions include Dirac's theorem (min degree ≥ n/2).</div>
    </div>
  );
}

// ── Diagram 6: Graph Coloring and Planarity ──────────────────────
function DiagramColoringPlanarity() {
  const W = 900, H = 360;
  const LP = 14, LW = 290;
  const MP = LP + LW + 14, MW = 280;
  const RP = MP + MW + 14, RW = W - RP - 14;
  const nr = 15;

  // Left: Petersen graph 3-coloring (actually K4 colored with 3 colors to show chromatic number)
  // Use a cycle C5 (needs 3 colors) positioned in left panel
  const c5Nodes = [0,1,2,3,4].map(i => ({
    x: LP + LW/2 + Math.round(Math.cos((i*2*Math.PI/5)-Math.PI/2)*64),
    y: 140 + Math.round(Math.sin((i*2*Math.PI/5)-Math.PI/2)*64),
    label: String.fromCharCode(65+i),
  }));
  const c5Edges = [[0,1],[1,2],[2,3],[3,4],[4,0]];
  const c5Colors = ['#c0392b','#2E75B6','#27ae60','#c0392b','#2E75B6']; // 3-coloring

  // Middle: K5 non-planar drawing (can't embed without crossings)
  // Draw K5 with one edge crossing highlighted
  const k5Nodes = [0,1,2,3,4].map(i => ({
    x: MP + MW/2 + Math.round(Math.cos((i*2*Math.PI/5)-Math.PI/2)*70),
    y: 140 + Math.round(Math.sin((i*2*Math.PI/5)-Math.PI/2)*70),
    label: String.fromCharCode(65+i),
  }));
  const k5Edges = [];
  for (let i=0;i<5;i++) for (let j=i+1;j<5;j++) k5Edges.push([i,j]);
  // Two edges that cross in any planar drawing
  const crossingEdges = new Set(['0-2','1-3']); // e.g. A-C and B-D

  // Right: K_{3,3} non-planar
  const k33L = [0,1,2].map(i => ({
    x: RP + 60, y: 70 + i*80, label: ['P','Q','R'][i],
  }));
  const k33R = [0,1,2].map(i => ({
    x: RP + RW - 60, y: 70 + i*80, label: ['X','Y','Z'][i],
  }));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Graph Coloring, Chromatic Number, and Planarity</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left: coloring */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Graph Coloring</text>
        <text x={LP+LW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">C5: chromatic number χ = 3</text>

        {c5Edges.map(([a,b],i) => (
          <line key={i} x1={c5Nodes[a].x} y1={c5Nodes[a].y}
            x2={c5Nodes[b].x} y2={c5Nodes[b].y} stroke="#888" strokeWidth="2"/>
        ))}
        {c5Nodes.map((n,i) => (
          <Node key={i} x={n.x} y={n.y} label={n.label} r={nr}
            fill={c5Colors[i]} stroke="#fff" textFill="#fff" fontSize={12}/>
        ))}

        {/* Coloring legend */}
        {['Red','Blue','Green'].map((name,i) => {
          const clr = ['#c0392b','#2E75B6','#27ae60'][i];
          return (
            <g key={i}>
              <circle cx={LP+20} cy={222+i*20} r={7} fill={clr}/>
              <text x={LP+32} y={226+i*20} fontSize="11" fill={clr} fontWeight="700">{name}</text>
            </g>
          );
        })}

        {/* Coloring facts */}
        <rect x={LP+8} y={280} width={LW-16} height={64} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+16} y={296} fontSize="11" fill="#1F3864" fontWeight="700">Graph coloring rules:</text>
        <text x={LP+16} y={312} fontSize="10" fill="#444">Adjacent vertices must have different colors</text>
        <text x={LP+16} y={327} fontSize="10" fill="#444">χ(G) = min colors needed (chromatic number)</text>
        <text x={LP+16} y={342} fontSize="10" fill="#444">χ(bipartite) = 2;  χ(odd cycle) = 3;  χ(Kn) = n</text>

        {/* Middle: K5 non-planar */}
        <rect x={MP} y={10} width={MW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={MP+MW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">K5 is Non-Planar</text>
        <text x={MP+MW/2} y={44} textAnchor="middle" fontSize="11" fill="#c0392b">Every drawing has at least one crossing</text>

        {k5Edges.map(([a,b],i) => {
          const key = `${a}-${b}`;
          const isCross = crossingEdges.has(key);
          return (
            <line key={i}
              x1={k5Nodes[a].x} y1={k5Nodes[a].y}
              x2={k5Nodes[b].x} y2={k5Nodes[b].y}
              stroke={isCross ? '#c0392b' : '#9b59b6'}
              strokeWidth={isCross ? 2.5 : 1.6}
              strokeDasharray={isCross ? '5,3' : undefined}/>
          );
        })}
        {k5Nodes.map((n,i) => (
          <Node key={i} x={n.x} y={n.y} label={n.label} r={nr}
            fill="#f5d0ff" stroke="#9b59b6" textFill="#6c2fa0"/>
        ))}

        {/* Euler formula */}
        <rect x={MP+8} y={236} width={MW-16} height={100} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={MP+16} y={252} fontSize="11" fill="#9b59b6" fontWeight="700">Euler's formula for planar graphs:</text>
        <text x={MP+16} y={268} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">V - E + F = 2</text>
        <text x={MP+16} y={284} fontSize="10" fill="#444">V = vertices, E = edges, F = faces (incl. outer)</text>
        <text x={MP+16} y={300} fontSize="10" fill="#444">For simple planar graph: E ≤ 3V - 6</text>
        <text x={MP+16} y={316} fontSize="10" fill="#444">K5: V=5, E=10, but 10 &gt; 3(5)-6=9  → non-planar</text>
        <text x={MP+16} y={330} fontSize="10" fill="#c0392b" fontWeight="700">Kuratowski: planar iff no K5 or K3,3 subdivision</text>

        {/* Right: K3,3 non-planar */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">K3,3 is Non-Planar</text>
        <text x={RP+RW/2} y={44} textAnchor="middle" fontSize="11" fill="#c0392b">Utility problem: 3 houses, 3 utilities</text>

        {k33L.map((nl,li) =>
          k33R.map((nr2,ri) => {
            const mx = (nl.x+nr2.x)/2, my = (nl.y+nr2.y)/2;
            const isCross = (li===0&&ri===2)||(li===2&&ri===0);
            return (
              <line key={`${li}-${ri}`}
                x1={nl.x+nr} y1={nl.y} x2={nr2.x-nr} y2={nr2.y}
                stroke={isCross ? '#c0392b' : '#e07b00'}
                strokeWidth={isCross ? 2.2 : 1.6}
                strokeDasharray={isCross ? '5,3' : undefined}/>
            );
          })
        )}
        {k33L.map((n,i) => (
          <Node key={'L'+i} x={n.x} y={n.y} label={n.label} r={nr}
            fill="#fff7ee" stroke="#e07b00" textFill="#a05000"/>
        ))}
        {k33R.map((n,i) => (
          <Node key={'R'+i} x={n.x} y={n.y} label={n.label} r={nr}
            fill="#fff7ee" stroke="#e07b00" textFill="#a05000"/>
        ))}
        <text x={RP+54} y={260} fontSize="10" fill="#e07b00" fontWeight="700">Houses</text>
        <text x={RP+RW-60} y={260} fontSize="10" fill="#e07b00" fontWeight="700">Utilities</text>

        {/* Four color theorem */}
        <rect x={RP+8} y={270} width={RW-16} height={68} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={RP+16} y={286} fontSize="11" fill="#e07b00" fontWeight="700">Four Color Theorem:</text>
        <text x={RP+16} y={302} fontSize="10" fill="#444">Any planar graph can be properly colored</text>
        <text x={RP+16} y={316} fontSize="10" fill="#444">with at most 4 colors.</text>
        <text x={RP+16} y={332} fontSize="10" fill="#555">Proved 1976 (Appel + Haken) by computer.</text>
        <text x={RP+16} y={347} fontSize="10" fill="#555">First major theorem proved with computer aid.</text>

      </svg>
      <div style={S.dCaption}>Graph coloring assigns colors to vertices so no two adjacent vertices share a color; the chromatic number χ(G) is the minimum number of colors needed. A graph is planar if it can be drawn in the plane without edge crossings; Euler's formula V-E+F=2 and the bound E≤3V-6 give quick non-planarity tests. Kuratowski's theorem: a graph is planar iff it contains no subdivision of K5 or K3,3. The Four Color Theorem states every planar graph needs at most 4 colors.</div>
    </div>
  );
}

// ── Diagram 7: Shortest Paths & Network Flow ─────────────────────
function DiagramShortestPath() {
  const W = 900, H = 340;
  const LP = 14, LW = 450;
  const RP = LP + LW + 14, RW = W - RP - 14;
  const nr = 15;

  // Dijkstra on a 5-node weighted graph
  const spNodes = [
    { x:LP+50,      y:85,  label:'s', dist:'0' },
    { x:LP+170,     y:55,  label:'A', dist:'4' },
    { x:LP+170,     y:155, label:'B', dist:'2' },
    { x:LP+310,     y:55,  label:'C', dist:'7' },
    { x:LP+310,     y:155, label:'D', dist:'5' },
    { x:LP+420,     y:105, label:'t', dist:'9' },
  ];
  const spEdges = [
    [0,1,4],[0,2,2],
    [1,2,5],[1,3,3],
    [2,4,3],
    [3,5,2],[4,3,0],[4,5,4],
  ];
  // Shortest path s→t: s→B(2)→D(5)→C(7)→t(9)
  const spPath = new Set(['0-2','2-4','4-3','3-5']);

  // Right: algorithm comparison table
  const algos = [
    { name:"Dijkstra's",    complex:"O((V+E) log V)", cond:"Non-negative weights", use:"GPS, network routing",   color:'#2E75B6', bg:'#EBF3FB', bc:'#2E75B6' },
    { name:"Bellman-Ford",  complex:"O(VE)",           cond:"Allows negative weights", use:"Currency arbitrage, routing", color:'#27ae60', bg:'#f0fff4', bc:'#27ae60' },
    { name:"Floyd-Warshall",complex:"O(V^3)",          cond:"All-pairs shortest paths", use:"Dense graphs, transitive closure", color:'#e07b00', bg:'#fff7ee', bc:'#e07b00' },
    { name:"A* Search",     complex:"O(E log V)*",     cond:"With admissible heuristic", use:"Game pathfinding, maps", color:'#9b59b6', bg:'#f5d0ff', bc:'#9b59b6' },
    { name:"Topological SP",complex:"O(V+E)",          cond:"DAG only (no cycles)", use:"Project scheduling, PERT", color:'#c0392b', bg:'#fff0f0', bc:'#c0392b' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Shortest Paths — Dijkstra's Algorithm and Algorithm Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Dijkstra's Algorithm</text>
        <text x={LP+LW/2} y={43} textAnchor="middle" fontSize="11" fill="#555">Shortest path from s to t  (path s→B→D→C→t, cost=9)</text>

        {/* All edges */}
        {spEdges.map(([a,b,w],i) => {
          const na = spNodes[a], nb = spNodes[b];
          const key = `${a}-${b}`;
          const isPath = spPath.has(key);
          const mx = (na.x+nb.x)/2, my = (na.y+nb.y)/2;
          const offX = (nb.y-na.y)*0.15, offY = -(nb.x-na.x)*0.15;
          return (
            <g key={i}>
              <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={isPath ? '#27ae60' : '#ccc'}
                strokeWidth={isPath ? 2.8 : 1.5}/>
              <rect x={mx+offX-8} y={my+offY-8} width={16} height={14} rx={2}
                fill={isPath ? '#f0fff4' : '#fff'}/>
              <text x={mx+offX} y={my+offY+2} textAnchor="middle" fontSize="11"
                fill={isPath ? '#27ae60' : '#bbb'}
                fontWeight={isPath ? '700' : '400'}
                fontFamily="'Courier New',monospace">{w}</text>
            </g>
          );
        })}

        {/* Nodes */}
        {spNodes.map((n,i) => {
          const isStart = n.label==='s', isEnd = n.label==='t';
          return (
            <g key={i}>
              <Node x={n.x} y={n.y} label={n.label} r={nr}
                fill={isStart?'#2E75B6':isEnd?'#27ae60':'#EBF3FB'}
                stroke={isStart?'#1F3864':isEnd?'#1a6640':'#2E75B6'}
                textFill={isStart||isEnd?'#fff':'#1F3864'}/>
              <rect x={n.x-10} y={n.y-nr-18} width={20} height={14} rx={2}
                fill={isStart?'#2E75B6':isEnd?'#27ae60':'#fff7ee'}/>
              <text x={n.x} y={n.y-nr-8} textAnchor="middle" fontSize="10"
                fill={isStart||isEnd?'#fff':'#e07b00'}
                fontWeight="700" fontFamily="'Courier New',monospace">d={n.dist}</text>
            </g>
          );
        })}

        {/* Dijkstra steps */}
        <rect x={LP+10} y={238} width={LW-20} height={86} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+18} y={254} fontSize="11" fill="#1F3864" fontWeight="700">Dijkstra steps (greedy — always extract min-dist unvisited):</text>
        {[
          'Init: dist[s]=0, all others = ∞',
          'Visit s: relax → dist[A]=4, dist[B]=2',
          'Visit B (dist=2): relax → dist[D]=5',
          'Visit D (dist=5): relax → dist[C]=5+0=5... wait, dist[C]=min(4+3,5)=7',
          'Visit A (dist=4): dist[C]=min(7,4+3)=7  ✓',
          'Visit C (dist=7): dist[t]=7+2=9  ✓',
        ].map((step,i) => (
          <text key={i} x={LP+18} y={268+i*14} fontSize="9.5" fill="#333"
            fontFamily="'Courier New',monospace">{step}</text>
        ))}

        {/* Right panel */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Algorithm Comparison</text>

        {algos.map(({ name, complex, cond, use, color, bg, bc }, i) => {
          const y = 36 + i * 58;
          return (
            <g key={i}>
              <rect x={RP+8} y={y} width={RW-16} height={52} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
              <text x={RP+16} y={y+15} fontSize="12" fill={color} fontWeight="700">{name}</text>
              <text x={RP+16} y={y+29} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{complex}  |  {cond}</text>
              <text x={RP+16} y={y+43} fontSize="10" fill="#666">Uses: {use}</text>
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>Dijkstra's algorithm finds shortest paths from a source by greedily visiting the closest unfinished vertex and relaxing its outgoing edges; it requires non-negative weights and runs in O((V+E) log V) with a priority queue. Bellman-Ford handles negative weights (O(VE)) and detects negative cycles. Floyd-Warshall finds all-pairs shortest paths in O(V³) — practical for small dense graphs. A* adds a heuristic to guide Dijkstra toward a target.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function GraphTheory() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Graph Theory in Discrete Mathematics</h1>
      <p style={S.p}>A graph G = (V, E) is a pair of a set of vertices (nodes) V and a set of edges E connecting pairs of them. Graph theory provides the mathematical language for modelling relationships: road networks, social connections, dependency structures, circuit layouts, and the internet are all graphs. The subject covers how to represent and store graphs, how to traverse and search them, special structural properties (trees, planarity, connectivity), and optimization problems (shortest paths, spanning trees, coloring). Many of the most important problems in computer science — from routing to scheduling to chip layout — reduce to graph-theoretic questions.</p>

      <InfoBox title="Core Idea: G = (V, E) — vertices connected by edges; structure determines what is computable" items={[
        "Undirected graph: edges are unordered pairs {u,v}; directed graph (digraph): ordered pairs (u,v)",
        "Degree: number of edges at a vertex; handshaking lemma: sum of degrees = 2|E|",
        "Path: sequence of distinct vertices connected by edges; cycle: path that starts and ends at the same vertex",
        "Connected: there exists a path between every pair of vertices; tree: connected + acyclic (|E|=|V|-1)",
        "BFS: shortest paths in unweighted graphs (queue); DFS: cycle detection, topological sort (stack/recursion)",
        "Planarity: K5 and K3,3 are the two minimal non-planar graphs (Kuratowski's theorem)",
      ]}/>

      <h2 style={S.h2}>Graph Types</h2>
      <DiagramGraphTypes/>
      <Table2 rows={[
        ["Undirected graph",    "Edges are unordered pairs {u,v} = {v,u}\nDeg(v) = number of edges incident to v"],
        ["Directed graph",     "Edges are ordered pairs (u,v); u→v but not necessarily v→u\nIn-degree indeg(v) and out-degree outdeg(v) tracked separately"],
        ["Weighted graph",     "Each edge (u,v) has a weight w(u,v) ∈ ℝ\nUsed for distances, costs, capacities, probabilities"],
        ["Simple graph",       "No self-loops, no parallel edges between the same pair\nMost theoretical results assume simple graphs"],
        ["Complete graph Kn",  "Every pair of vertices is connected: |E| = n(n-1)/2\nChromatic number χ(Kn) = n; always Hamiltonian"],
        ["Bipartite graph",    "Vertices split into sets L, R; edges only cross between L and R\nBipartite iff it contains no odd-length cycle; χ = 2"],
        ["Subgraph",           "G' = (V', E') with V' ⊆ V, E' ⊆ E restricted to V'\nSpanning subgraph: V' = V (keeps all vertices)"],
      ]}/>

      <h2 style={S.h2}>Graph Representations</h2>
      <DiagramRepresentations/>
      <Table2 rows={[
        ["Adjacency matrix",   "n×n 0/1 matrix A where A[i][j]=1 iff edge (i,j) exists\nSpace O(V²); O(1) lookup; symmetric for undirected"],
        ["Adjacency list",     "Array of V linked lists; list[v] = neighbors of v\nSpace O(V+E); O(deg(v)) neighbor iteration; best for sparse"],
        ["Edge list",          "List of pairs (u,v) [and weight w]\nSpace O(E); simple but slow for neighbor queries"],
        ["Incidence matrix",   "V×E matrix; entry (v,e) = 1 if v is endpoint of e\nUsed in network flow and linear algebra of graphs"],
        ["Handshaking lemma",  "Σ deg(v) = 2|E|  for undirected graphs\nEvery edge contributes 1 to each of its two endpoints"],
        ["Sparse vs dense",    "Sparse: |E| = O(V); dense: |E| = O(V²)\nChoose adjacency list for sparse, matrix for dense"],
      ]}/>

      <h2 style={S.h2}>BFS and DFS Traversal</h2>
      <DiagramBFSDFS/>
      <Table2 rows={[
        ["Breadth-First Search","Visits all neighbors at distance k before distance k+1\nUses a FIFO queue; produces BFS tree with shortest-path edges"],
        ["BFS shortest path",  "dist[v] = level of v in BFS tree = shortest hop-count from source\nWorks for unweighted graphs; O(V+E)"],
        ["Depth-First Search", "Explores as deep as possible before backtracking\nUses LIFO stack (or recursion); O(V+E)"],
        ["DFS tree edges",     "Tree edge: u→v first discovery; Back edge: u→ancestor (cycle!)\nForward/cross edges also arise in directed graphs"],
        ["Topological sort",   "DFS on DAG; output vertices in reverse finishing-time order\nValid only if graph is a DAG (no back edges)"],
        ["Connected components","Run BFS/DFS from each unvisited vertex; each run = one component\nFor directed: Kosaraju's or Tarjan's for SCCs in O(V+E)"],
        ["Bipartiteness check","BFS with 2-coloring: if we need a 3rd color, graph is not bipartite\nGraph is bipartite iff BFS finds no odd-length cycle"],
      ]}/>

      <h2 style={S.h2}>Trees and Spanning Trees</h2>
      <DiagramTrees/>
      <Table2 rows={[
        ["Tree",               "Connected acyclic graph: |E| = |V| - 1\nAny two of: connected, acyclic, |E|=|V|-1 imply the third"],
        ["Rooted tree",        "One vertex designated root; edges directed away from root\nDepth = distance from root; height = max depth in tree"],
        ["Spanning tree",      "Spanning subgraph that is a tree (all V vertices, V-1 edges)\nA connected graph on n vertices has at least one spanning tree"],
        ["Minimum spanning tree","Spanning tree with smallest total edge weight\nGreedy algorithms (Kruskal, Prim) find MST optimally"],
        ["Kruskal's algorithm","Sort edges by weight; add each edge if it doesn't form a cycle\nUses Union-Find (disjoint sets); O(E log E)"],
        ["Prim's algorithm",   "Grow MST from seed; always add cheapest edge crossing the cut\nPriority queue; O(E log V) with binary heap"],
        ["Number of trees",    "Cayley's formula: K_n has n^(n-2) spanning trees\nKirchhoff matrix-tree theorem: count via determinant of Laplacian"],
      ]}/>

      <h2 style={S.h2}>Euler and Hamiltonian Paths</h2>
      <DiagramEulerHamilton/>
      <Table2 rows={[
        ["Euler path",         "Walk using every edge exactly once\nExists iff graph is connected and has exactly 0 or 2 odd-degree vertices"],
        ["Euler circuit",      "Euler path that starts and ends at the same vertex\nExists iff graph is connected and ALL vertices have even degree"],
        ["Fleury's algorithm", "Delete bridges only as a last resort; always traverse non-bridge first\nConstructs an Euler circuit in O(E²); Hierholzer's is O(E)"],
        ["Königsberg bridges", "4 landmasses, 7 bridges — all 4 have odd degree → no Euler circuit\nEuler's 1736 paper founded graph theory"],
        ["Hamiltonian path",   "Path visiting every vertex exactly once\nNo efficient criterion known; problem is NP-complete"],
        ["Hamiltonian circuit","Hamiltonian path returning to start vertex\nDirac: if every vertex has degree ≥ n/2, circuit exists"],
        ["Euler vs Hamiltonian","Euler: every EDGE once (polynomial); Hamilton: every VERTEX once (NP-hard)\nFundamentally different computational difficulty"],
      ]}/>

      <h2 style={S.h2}>Graph Coloring and Planarity</h2>
      <DiagramColoringPlanarity/>
      <Table2 rows={[
        ["Proper coloring",    "Assign colors to vertices so no two adjacent vertices share a color\nChromatic number χ(G) = minimum colors needed"],
        ["Chromatic number",   "χ(bipartite) = 2;  χ(odd cycle C_{2k+1}) = 3;  χ(K_n) = n\nGreedy coloring gives χ ≤ Δ+1 (Δ = max degree)"],
        ["Planar graph",       "Can be drawn in the plane with no edge crossings\nEvery planar graph has a vertex of degree ≤ 5"],
        ["Euler's formula",    "V - E + F = 2 for any connected planar graph\nF includes the unbounded outer face"],
        ["Planarity test",     "Simple planar graph: E ≤ 3V-6; bipartite planar: E ≤ 2V-4\nViolating these → non-planar (necessary but not sufficient alone)"],
        ["Kuratowski's theorem","G is planar iff it contains no subdivision of K5 or K3,3\nComplete characterisation of planarity"],
        ["Four Color Theorem", "Every planar graph is 4-colorable (Appel-Haken, 1976)\nFirst major theorem proved with a computer; 1936 cases verified"],
      ]}/>

      <h2 style={S.h2}>Shortest Paths</h2>
      <DiagramShortestPath/>
      <Table2 rows={[
        ["Shortest path problem","Find minimum-weight path from source s to target t\nApplications: GPS routing, network packets, project scheduling"],
        ["Dijkstra's algorithm","Greedy: always relax edges from the nearest unvisited vertex\nO((V+E) log V) with binary heap; requires non-negative weights"],
        ["Bellman-Ford",       "Relax all edges V-1 times; detect negative cycles on Vth pass\nO(VE); handles negative weights; detects negative-weight cycles"],
        ["Floyd-Warshall",     "Dynamic programming all-pairs: d[i][j] = min via intermediate k\nO(V³) time and O(V²) space; good for dense graphs"],
        ["Relaxation",         "If dist[u] + w(u,v) < dist[v] then dist[v] = dist[u] + w(u,v)\nAll shortest-path algorithms are variants of this update rule"],
        ["Negative cycles",    "A cycle whose total weight is negative: shortest path undefined\nBellman-Ford detects them; Dijkstra silently gives wrong answers"],
        ["DAG shortest path",  "Topological sort + single pass of relaxation: O(V+E)\nFastest for directed acyclic graphs; also works with negative weights"],
      ]}/>

      <InfoBox title="Key Takeaway — Graph Theory Quick Reference" items={[
        "Store sparse graphs as adjacency lists O(V+E); dense graphs as adjacency matrices O(V²)",
        "BFS → shortest hop-count paths and bipartiteness; DFS → cycles, topological sort, SCCs",
        "Tree = connected + acyclic = V vertices, V-1 edges; MST via Kruskal O(E log E) or Prim O(E log V)",
        "Euler circuit exists iff all degrees even; Hamiltonian circuit is NP-complete",
        "Planar: V-E+F=2; E ≤ 3V-6; non-planar iff contains K5 or K3,3 subdivision (Kuratowski)",
        "χ(G) = chromatic number; bipartite → 2; odd cycle → 3; K_n → n; planar → 4 (Four Color Thm)",
        "Dijkstra O((V+E) log V) for non-negative weights; Bellman-Ford O(VE) for negative weights",
        "Floyd-Warshall O(V³) all-pairs; topological DAG shortest path O(V+E)",
      ]}/>
    </div>
  );
}